import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { VideoItem } from "@/types/video";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import Seo from "@/components/feature/Seo";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";
import Toast from "@/components/feature/Toast";
import { formatDate, formatViews } from "@/utils/format";

export default function Detail() {
  const { videoId } = useParams();
  const [toast, setToast] = useState<string | null>(null);

  const video = videos.find((item) => item.id === videoId) as VideoItem | undefined;
  const category = video ? categories.find((item) => item.id === video.categoryId) : undefined;

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2200);
  };

  if (!video) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-file-unknow-line"
          title="视频资源已下架或不存在"
          description="你访问的视频条目可能已被移除，或链接地址有误，可以返回首页浏览其他内容。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const related: VideoItem[] = videos
    .filter((item) => item.id !== video.id)
    .map((item) => {
      const sameCategory = item.categoryId === video.categoryId ? 2 : 0;
      const sharedTags = item.tags.filter((tag) => video.tags.includes(tag)).length;
      return { item: item as VideoItem, score: sameCategory + sharedTags + item.views / 1000000 };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((entry) => entry.item);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("页面链接已复制");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleResourceNav = (target: string) => {
    showToast(`即将跳转到外部「${target}」（本站仅为资源导航演示，不提供真实播放或下载）`);
  };

  const metaList = [
    { label: "所属分类", value: category?.name ?? "未分类" },
    { label: "地区", value: video.region },
    { label: "年份", value: video.year },
    { label: "清晰度", value: video.quality },
    { label: "时长", value: video.duration },
    { label: "上传日期", value: formatDate(video.uploadDate) },
    { label: "访问量", value: formatViews(video.views) },
  ];

  return (
    <>
      <Seo
        title={`${video.title}-视频详情 - 黄瓜视频下载`}
        description={`${video.title}：${video.summary} 分类：${category?.name ?? "视频"}，地区 ${video.region}，清晰度 ${video.quality}。`}
        keywords={`黄瓜视频下载,${video.title},${video.tags.join(",")},视频详情`}
      />

      <div className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6 md:pt-8">
        <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500 md:text-sm" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          {category ? (
            <>
              <Link to={`/category/${category.id}`} className="transition-colors hover:text-primary-600">
                {category.name}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
            </>
          ) : null}
          <span className="line-clamp-1 text-foreground-900">{video.title}</span>
        </nav>

        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
              <img
                src={video.cover}
                alt={video.title}
                title={`${video.title} 视频封面`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-foreground-950/40">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-50/90 text-primary-600">
                  <i className="ri-play-fill text-3xl"></i>
                </span>
              </div>
              <span className="absolute bottom-3 left-3 rounded-md bg-foreground-950/70 px-2 py-1 text-xs text-white">
                演示封面 · 非真实播放
              </span>
            </div>

            <div className="mt-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-medium text-white">{video.quality}</span>
                {video.tags.map((tag) => (
                  <span key={tag} className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="mt-3 font-heading text-xl font-bold text-foreground-950 md:text-3xl">{video.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {formatDate(video.uploadDate)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {video.duration}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(video.views)} 次浏览
                </span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => handleResourceNav("在线观看平台")}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3.5 text-base font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-xl"></i>
                前往在线观看
              </button>
              <button
                type="button"
                onClick={() => handleResourceNav("高清下载页面")}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3.5 text-base font-medium text-white transition-colors hover:bg-accent-600"
              >
                <i className="ri-download-2-line text-xl"></i>
                前往高清下载
              </button>
            </div>
            <p className="mt-3 flex items-start gap-2 rounded-md bg-accent-50 px-3 py-2 text-xs leading-relaxed text-accent-800">
              <i className="ri-alert-line mt-0.5"></i>
              说明：以上按钮为外部资源导航指示，本站仅作内容聚合与导航展示，不实现真实播放或下载服务。
            </p>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">视频简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{video.description}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{video.summary}</p>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">视频信息</h2>
              <dl className="mt-4 space-y-3">
                {metaList.map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-3 text-sm">
                    <dt className="text-foreground-500">{item.label}</dt>
                    <dd className="text-right font-medium text-foreground-900">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {category ? (
              <Link
                to={`/category/${category.id}`}
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-xl`}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">所属分类</p>
                  <p className="font-heading text-sm font-semibold text-foreground-950">{category.name}</p>
                </div>
                <i className="ri-arrow-right-s-line ml-auto text-foreground-400"></i>
              </Link>
            ) : null}

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">分享视频</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-link"></i>
                  复制链接
                </button>
                <button
                  type="button"
                  onClick={() => showToast("已唤起微博分享（前端演示）")}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  aria-label="分享到微博"
                >
                  <i className="ri-weibo-line"></i>
                </button>
                <button
                  type="button"
                  onClick={() => showToast("已唤起微信分享（前端演示）")}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  aria-label="分享到微信"
                >
                  <i className="ri-wechat-line"></i>
                </button>
                <button
                  type="button"
                  onClick={() => showToast("已复制分享文案（前端演示）")}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  aria-label="复制分享文案"
                >
                  <i className="ri-file-copy-line"></i>
                </button>
              </div>
            </div>
          </aside>
        </div>

        {related.length > 0 ? (
          <section className="mt-12">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-6 w-1 rounded-full bg-primary-500"></span>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">相关推荐</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
              {related.map((item) => (
                <VideoCard key={item.id} video={item} />
              ))}
            </div>
          </section>
        ) : null}
      </div>

      <Toast message={toast} />
    </>
  );
}